import { htmlZuText } from '../domain/htmlText';
import { hatMarkup } from '../domain/htmlSicher';
import { frac, scaleStep, toNumber } from '../domain/points';
import type { Candidate, Criterion, Exam, Person, Station, Task, TaskScoring, Vignette } from '../domain/types';
import type { Store } from './port';
import { pruefungsEinstellungenRoh } from './pruefungsEinstellungen';
import type {
  ActorRow, CandidateRow, ExamActorRow, ExamAnswerRow, ExamCandidateRow, ExamExaminerRow,
  ExamItemContainerRow, ExamItemRow, ExamPartRow, ExamQuestionRow, ExamRow, ExaminerRow,
  MediaRow,
} from './rows';

/**
 * Aus den Tabellen wieder das machen, was der Bogen anzeigt.
 *
 * ⚠️ **Die Bewertungsart ist abgeleitet, nicht gespeichert.** Relay hat am `ExamItem` kein Feld
 * dafür (Spec §5.2, §15): keine `ExamAnswer` heißt globale Bewertung, `ExamAnswer` ohne
 * `scalaValues` heißt Checkliste, mit `scalaValues` heißt bewertete Kriterien. Bekommt Relay
 * ein Feld, ersetzt es diese Funktion.
 *
 * ⚠️ **Keine Tabelle hier liefert von sich aus eine verlässliche Reihenfolge.** Das gilt für
 * beide Adapter, nicht nur für einen: der Web-Adapter (`web.ts`) holt über
 * `IDBObjectStore.getAll()`, was IndexedDB nach Primärschlüssel sortiert liefert; der native
 * Adapter (`sqlite.ts`) setzt `SELECT * FROM table` ohne `ORDER BY` ab, worauf SQLite je nach
 * gewähltem Index ebenso gut nach einer Fremdschlüsselspalte sortieren kann. Wo Relay im
 * Schema (`RELAY/docs/schema/METIS-schema.md`) ein `ORDER BY` vorschreibt, wird deshalb hier
 * explizit sortiert, statt sich auf die Rückgabefolge zu verlassen — für Station und Aufgabe
 * ist das `orderNo` (unten `nachOrder`), für Prüfer/Schauspieler `lastName, firstName`
 * (`nachName`), für Prüflinge `examCandidateNo` (`nachExamCandidateNo`).
 */

export function scoringKindOf(answers: ExamAnswerRow[]): 'global' | 'checklist' | 'criteria' {
  if (answers.length === 0) return 'global';
  return answers.some((a) => a.scalaValues) ? 'criteria' : 'checklist';
}

const nachOrder = <T extends { orderNo: number }>(rows: T[]) =>
  [...rows].sort((a, b) => a.orderNo - b.orderNo);

/** Relay: `Examiner` und `Actor` sind je `ORDER BY lastName ASC, firstName ASC`. */
const nachName = <T extends { lastName: string; firstName: string }>(rows: T[]) =>
  [...rows].sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));

/**
 * `examCandidateNo` als Zahl, für den Vergleich in `nachExamCandidateNo`. `NULL` ist laut
 * Schema erlaubt, und auch ein nicht-numerischer Wert wäre technisch möglich — beides macht
 * die Zeile ordnungslos. Sie landet dann ans Ende (`Infinity`), statt zufällig an den
 * lexikografischen Anfang zu rutschen, wie es ein Textvergleich mit `""` täte.
 */
const alsLaufnummer = (s: string | null): number => {
  if (s === null) return Number.POSITIVE_INFINITY;
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};

/**
 * Relay: `ExamCandidate` ist `ORDER BY examCandidateNo ASC` — die für *diese* Prüfung
 * vergebene laufende Nummer. Nicht zu verwechseln mit `Candidate.matriculationNumber`: die
 * Matrikelnummer ist eine Eigenschaft der Person, unabhängig von jeder Prüfung, und für
 * `Candidate` selbst schreibt Relay ohnehin `lastName ASC, firstName ASC` vor, nie die
 * Matrikelnummer. Verglichen wird numerisch (`alsLaufnummer`), nicht lexikografisch — sonst
 * stünde `"10"` vor `"9"`.
 */
const nachExamCandidateNo = (rows: ExamCandidateRow[]) =>
  [...rows].sort((a, b) => alsLaufnummer(a.examCandidateNo) - alsLaufnummer(b.examCandidateNo));

const name = (p: { firstName: string; lastName: string }) => (p.firstName + ' ' + p.lastName).trim();

/**
 * Klartext für Stellen, die keinen Satz mit Auszeichnung zeigen: Kopfzeile, Listen, Kicker,
 * die Aufzählung offener Aufgaben. Prüfungstexte dürfen HTML tragen (2026-10-02); angezeigt
 * wird es nur, wo `ui/HtmlText` steht.
 */
const klartext = (wert: string) => (hatMarkup(wert) ? htmlZuText(wert) : wert);

function scoringOf(item: ExamItemRow, frage: ExamQuestionRow, answers: ExamAnswerRow[]): TaskScoring {
  const art = scoringKindOf(answers);
  const max = toNumber(frac(item.maxPointsNumerator, item.maxPointsDenominator));

  if (art === 'global') {
    return { kind: 'global', max, body: frage.questionText ?? '' };
  }

  if (art === 'checklist') {
    return {
      kind: 'checklist',
      items: nachOrder(answers).map((a) => ({
        text: a.answerText ?? '',
        points: toNumber(frac(a.maxPointsNumerator, a.maxPointsDenominator)),
      })),
    };
  }

  const criteria: Criterion[] = nachOrder(answers).map((a) => {
    const stufen = JSON.parse(a.scalaValues ?? '[]') as string[];
    const höchst = frac(a.maxPointsNumerator, a.maxPointsDenominator);
    return {
      label: a.answerText ?? '',
      // Skalen sind linear: die Punkte einer Stufe ergeben sich aus Position und Höchstwert.
      options: stufen.map((label, i) => ({ label, points: toNumber(scaleStep(höchst, i, stufen.length)) })),
    };
  });
  return { kind: 'criteria', criteria };
}

export async function readExam(store: Store, examId: string): Promise<Exam> {
  const [exam] = await store.all<ExamRow>('Exam', { id: examId });
  if (!exam) throw new Error('Prüfung unbekannt: ' + examId);

  const parts = nachOrder(await store.all<ExamPartRow>('ExamPart', { examID: examId }));
  const stations: Station[] = [];

  for (const part of parts) {
    const container = nachOrder(
      await store.all<ExamItemContainerRow>('ExamItemContainer', { examPartID: part.id }),
    );
    for (const c of container) {
      const items = nachOrder(await store.all<ExamItemRow>('ExamItem', { examItemContainerID: c.id }));
      const tasks: Task[] = [];
      for (const item of items) {
        const [frage] = await store.all<ExamQuestionRow>('ExamQuestion', { examItemID: item.id });
        // Ohne `ExamQuestion` wird die Aufgabe übersprungen — dieselbe Entscheidung wie in
        // `repos.ts::buildTaskIndex`, und sie muss dieselbe sein: was dort nicht in den Index
        // kommt, lässt sich nie ins Protokoll schreiben. Eine Aufgabe anzuzeigen, deren
        // Bewertung stillschweigend nirgends landet, wäre der teurere Fehler.
        //
        // Vorher fehlte diese Wache hier: `scoringOf(item, frage, [])` griff im Zweig
        // „globale Bewertung" auf `frage.questionText` zu und warf einen TypeError — die
        // ganze Prüfung wäre an einer einzigen unvollständigen Aufgabe unlesbar geworden
        // (`hydrate.ts` fängt das ab und zeigt dem Prüfer nur noch `startupFailed`).
        // `noUncheckedIndexedAccess` ist aus, der Typecheck meldete deshalb nichts.
        if (!frage) continue;
        const answers = await store.all<ExamAnswerRow>('ExamAnswer', { examQuestionID: frage.id });
        tasks.push({
          id: item.id,
          // Der Titel darf Auszeichnung tragen (`TaskCard` zeigt sie), die Kurzbezeichnung nicht.
          title: item.name,
          short: klartext(item.anchorCriterion ?? item.name),
          typeLabel: item.comment ?? '',
          scoring: scoringOf(item, frage, answers),
          ...(typeof item.pointsStep === 'number' && item.pointsStep > 0 ? { pointsStep: item.pointsStep } : {}),
        });
      }
      // Die Anhänge der Station. `Media` führt sie über `referenceType`/`referenceIdentifier`
      // — dieselbe Zeile trägt auch Anhänge an Prüfung, Aufgabe oder Prüfling, deshalb muss
      // **beides** geprüft werden.
      const medienZeilen = nachOrder(
        (await store.all<MediaRow>('Media', { referenceIdentifier: c.id })).filter(
          (m) => m.referenceType === 'EXAM_ITEM_CONTAINER',
        ),
      );

      stations.push({
        id: c.id,
        label: klartext(c.name),
        meta: c.comment ?? '',
        durationSeconds: c.durationSeconds ?? 600,
        vignette: c.vignette ? (JSON.parse(c.vignette) as Vignette) : undefined,
        medien: medienZeilen.map((m) => ({
          id: m.id,
          name: m.name,
          description: m.description ?? '',
          mimeType: m.mimeType ?? '',
          istBild: (m.mimeType ?? '').startsWith('image/'),
        })),
        tasks,
      });
    }
  }

  const prüferBezug = await store.all<ExamExaminerRow>('ExamExaminer', { examID: examId });
  const prüferZeilen: ExaminerRow[] = [];
  for (const bezug of prüferBezug) {
    const [zeile] = await store.all<ExaminerRow>('Examiner', { id: bezug.examinerID });
    if (zeile) prüferZeilen.push(zeile);
  }
  const examiners: Person[] = nachName(prüferZeilen).map((zeile) => ({
    id: zeile.id,
    name: name(zeile),
    meta: zeile.groupName,
  }));

  const schauspielerBezug = await store.all<ExamActorRow>('ExamActor', { examID: examId });
  const schauspielerZeilen: ActorRow[] = [];
  for (const bezug of schauspielerBezug) {
    const [zeile] = await store.all<ActorRow>('Actor', { id: bezug.actorID });
    if (zeile) schauspielerZeilen.push(zeile);
  }
  const actors: Person[] = nachName(schauspielerZeilen).map((zeile) => ({
    id: zeile.id,
    name: name(zeile),
    meta: zeile.groupName ?? '',
  }));

  const prüflingBezug = nachExamCandidateNo(
    await store.all<ExamCandidateRow>('ExamCandidate', { examID: examId }),
  );
  const candidates: Candidate[] = [];
  for (const bezug of prüflingBezug) {
    const [zeile] = await store.all<CandidateRow>('Candidate', { id: bezug.candidateID });
    if (!zeile) continue;
    candidates.push({
      // Die `id` ist die Relay-Kennung — sie wandert in `ExamLog.candidateID`. Die
      // Matrikelnummer steht daneben: sie ist gedruckt, nicht schlüsselführend.
      id: zeile.id,
      name: name(zeile),
      firstName: zeile.firstName,
      lastName: zeile.lastName,
      matriculationNumber: zeile.matriculationNumber ?? '',
      // Die laufende Nummer dieser Prüfung steht am **Bezug**, nicht an der Person: derselbe
      // Prüfling trägt in einer anderen Prüfung eine andere.
      examNo: bezug.examCandidateNo,
      // Zustand und Punktzahl kommen aus CandidateExamMetadata bzw. ExamLog, nicht von hier.
      state: 'wartet',
      score: null,
    });
  }

  return {
    id: exam.id,
    name: exam.name,
    meta: exam.description ?? '',
    loaded: true,
    // ⚠️ `'Y'` ist das einzige Ja. Ein fehlendes Feld (Classic, alte Ablage) ist damit „echt" —
    // im Zweifel wird aufgezeichnet, nicht verworfen.
    simulativ: exam.simulative === 'Y',
    vorschau: exam.preview === 'Y',
    stations,
    examiners,
    actors,
    candidates,
  };
}

export async function listExams(store: Store) {
  const alle = await store.all<ExamRow>('Exam');
  // `lastModifiedDate` als Stand: er wird beim Download aus dem `lastUpdate` des Servers
  // gesetzt (`classic/abbildung.ts`) und ist damit die Seite des Vergleichs, die dem Gerät
  // gehört (s. `domain/examCatalogue.ts::istVeraltet`).
  return alle.map((e) => ({
    id: e.id,
    name: e.name,
    meta: e.description ?? '',
    stand: e.lastModifiedDate,
    // Für die Markierung in der Prüfungsauswahl — sie muss sichtbar sein, **bevor** der Prüfer
    // die Prüfung öffnet (Vorgabe des Nutzers, 2026-09-18).
    simulativ: e.simulative === 'Y',
    vorschau: e.preview === 'Y',
    einstellungen: pruefungsEinstellungenRoh(e.settings),
  }));
}
